/**
 * Send Reminders Dialog Component
 * Allows users to send payment reminders for selected overdue invoices
 */

import { useState } from "react";
import { Mail, AlertCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { type Transaction } from "@/types/transaction";
import { formatCurrency, formatDateDDMMYYYY } from "@/lib/utils";

interface SendRemindersDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  transactions: Transaction[];
  onSend: (transactionIds: number[], message: string) => void;
}

export function SendRemindersDialog({
  open,
  onOpenChange,
  transactions,
  onSend,
}: SendRemindersDialogProps) {
  const [selectedTransactions, setSelectedTransactions] = useState<Set<number>>(
    new Set(transactions.map((t) => t.id))
  );
  const [customMessage, setCustomMessage] = useState("");
  const [isSending, setIsSending] = useState(false);

  // Filter for overdue invoices only
  const overdueInvoices = transactions.filter(
    (t) => t.transactionType === "invoice" && t.status === "overdue"
  );

  const handleToggleTransaction = (id: number) => {
    const newSelected = new Set(selectedTransactions);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedTransactions(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedTransactions.size === overdueInvoices.length) {
      setSelectedTransactions(new Set());
    } else {
      setSelectedTransactions(new Set(overdueInvoices.map((t) => t.id)));
    }
  };

  const handleSend = async () => {
    if (selectedTransactions.size === 0) return;

    setIsSending(true);
    
    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 1500));
    
    onSend(Array.from(selectedTransactions), customMessage);
    setIsSending(false);
    
    // Reset state
    setCustomMessage("");
    setSelectedTransactions(new Set(overdueInvoices.map((t) => t.id)));
    onOpenChange(false);
  };

  const defaultMessage = `Dear Customer,

This is a friendly reminder that the following invoice(s) are now overdue. We kindly request payment at your earliest convenience.

If you have already made payment, please disregard this message. If you have any questions or concerns, please don't hesitate to contact us.

Thank you for your business.`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Send Payment Reminders
          </DialogTitle>
          <DialogDescription>
            Send payment reminder emails to customers with overdue invoices
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Info Alert */}
          <Alert>
            <AlertDescription>
              Reminders will be sent to the email addresses associated with each customer account.
            </AlertDescription>
          </Alert>

          {/* Transaction Selection */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-base font-semibold">
                Select Invoices ({selectedTransactions.size} of {overdueInvoices.length})
              </Label>
              <Button
                variant="outline"
                size="sm"
                onClick={handleSelectAll}
              >
                {selectedTransactions.size === overdueInvoices.length
                  ? "Deselect All"
                  : "Select All"}
              </Button>
            </div>

            <div className="border rounded-lg divide-y max-h-64 overflow-y-auto">
              {overdueInvoices.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">
                  <AlertCircle className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No overdue invoices in selection</p>
                </div>
              ) : (
                overdueInvoices.map((transaction) => (
                  <div
                    key={transaction.id}
                    className="flex items-center gap-3 p-3 hover:bg-muted/50 transition-colors"
                  >
                    <Checkbox
                      checked={selectedTransactions.has(transaction.id)}
                      onCheckedChange={() => handleToggleTransaction(transaction.id)}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium">{transaction.referenceNumber}</span>
                        <Badge variant="destructive" className="text-xs">
                          Overdue
                        </Badge>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <span>{transaction.customerName}</span>
                        <span>•</span>
                        <span>Due: {formatDateDDMMYYYY(transaction.dueDate!)}</span>
                        <span>•</span>
                        <span className="font-medium text-foreground">
                          {formatCurrency(transaction.amount, transaction.currency)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Custom Message */}
          <div className="space-y-2">
            <Label htmlFor="custom-message">
              Custom Message (Optional)
            </Label>
            <Textarea
              id="custom-message"
              placeholder={defaultMessage}
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              rows={8}
              className="resize-none"
            />
            <p className="text-xs text-muted-foreground">
              Leave blank to use the default reminder message. Invoice details will be automatically included.
            </p>
          </div>

          {/* Summary */}
          {selectedTransactions.size > 0 && (
            <Alert variant="success" className="bg-green-50 border-green-200">
              <AlertDescription className="text-green-800">
                <strong>{selectedTransactions.size}</strong> reminder email
                {selectedTransactions.size !== 1 ? "s" : ""} will be sent
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            disabled={selectedTransactions.size === 0 || isSending}
            className="gap-2"
          >
            {isSending ? (
              <>
                <div className="h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Mail className="h-4 w-4" />
                Send {selectedTransactions.size} Reminder{selectedTransactions.size !== 1 ? "s" : ""}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
